import {
  Form,
  FormControl,
  FormLabel,
  FormItem,
  FormField,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm, useFieldArray, useFormContext } from "react-hook-form";
import { z } from "zod";
import {
  ArrowPathIcon,
  VariableIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  LockClosedIcon,
} from "@heroicons/react/16/solid";
import { useState } from "react";
import {
  type ManagedVariable,
  type ProjectVariable,
  projectVariableSchema,
} from "@imagine/ai-service/shared-types/route-types";

const variablesFormSchema = z.object({
  variables: z.array(projectVariableSchema),
});

type VariablesFormData = z.infer<typeof variablesFormSchema>;

type VariableItemProps = {
  index: number;
  onDelete: (index: number) => void;
  onEditingChange?: (isEditing: boolean) => void;
};

function VariableItem({ index, onDelete, onEditingChange }: VariableItemProps) {
  const { watch, setValue } = useFormContext<VariablesFormData>();
  const [isEditing, setIsEditing] = useState(false);
  const [isValueMasked, setIsValueMasked] = useState(true);
  const [editedKey, setEditedKey] = useState("");
  const [editedValue, setEditedValue] = useState("");

  const variable = watch(`variables.${index}`);
  const allVariables = watch("variables");

  const handleStartEdit = () => {
    setEditedKey(variable?.key || "");
    setEditedValue(variable?.value || "");
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    onEditingChange?.(false);
  };

  const handleSaveEdit = () => {
    if (isValid) {
      setValue(`variables.${index}.key`, trimmedKey, { shouldDirty: true });
      setValue(`variables.${index}.value`, trimmedValue, { shouldDirty: true });
      setIsEditing(false);
      onEditingChange?.(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) {
      e.preventDefault();
      handleSaveEdit();
    }
  };

  // Validation for edited values
  const trimmedKey = editedKey.trim();
  const trimmedValue = editedValue.trim();
  const isKeyEmpty = trimmedKey === "";
  const isValueEmpty = trimmedValue === "";

  // Check for duplicates (excluding the current variable)
  const isDuplicate = allVariables.some(
    (v, i) => i !== index && v.key === trimmedKey,
  );

  const isValid = !isKeyEmpty && !isValueEmpty && !isDuplicate;
  const keyError = !isKeyEmpty && isDuplicate;

  return (
    <div className="flex items-center gap-1 h-8 flex-shrink-0 group">
      {/* Icon */}
      <div className="flex-shrink-0 flex items-center justify-center mr-2">
        <VariableIcon className="size-4 text-muted-foreground" />
      </div>

      {/* Variable Name */}
      {isEditing ? (
        <div className="flex-shrink-0 w-45">
          <Input
            value={editedKey}
            onChange={(e) => setEditedKey(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`h-7 text-sm font-mono ${keyError ? "border-destructive" : ""}`}
          />
        </div>
      ) : (
        <div className="flex-shrink-0 w-45 truncate">
          <span className="text-sm font-mono text-foreground">
            {variable?.key}
          </span>
        </div>
      )}

      {/* Masked Value */}
      {isEditing ? (
        <div className="flex-1 min-w-0">
          <Input
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-7 text-sm font-mono"
          />
        </div>
      ) : (
        <div className="flex-1 min-w-0 truncate">
          {isValueMasked ? (
            <span className="text-sm font-mono text-foreground">*******</span>
          ) : (
            <span className="text-sm font-mono text-foreground">
              {variable?.value}
            </span>
          )}
        </div>
      )}

      {/* Actions */}
      {!isEditing ? (
        <div className="items-center gap-1 hidden group-hover:flex">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsValueMasked(!isValueMasked)}
            className="size-6 text-muted-foreground hover:text-foreground"
            type="button"
          >
            {isValueMasked ? (
              <EyeIcon className="size-3.5" />
            ) : (
              <EyeSlashIcon className="size-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleStartEdit}
            className="size-6 text-muted-foreground hover:text-foreground"
            type="button"
          >
            <PencilSquareIcon className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(index)}
            className="size-6 text-muted-foreground hover:text-foreground"
            type="button"
          >
            <TrashIcon className="size-3.5 text-destructive" />
          </Button>
        </div>
      ) : (
        <div className="items-center gap-1 flex">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancelEdit}
            className="size-6"
            type="button"
          >
            <XMarkIcon className="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSaveEdit}
            className="size-6"
            type="button"
            disabled={!isValid}
          >
            <CheckIcon
              className={`size-3.5 ${isValid ? "text-green-600" : "text-muted-foreground"}`}
            />
          </Button>
        </div>
      )}
    </div>
  );
}

function ManagedVariableItem({ variableKey }: { variableKey: string }) {
  return (
    <div className="flex items-center gap-1 h-8 flex-shrink-0 group">
      {/* Icon */}
      <div className="flex-shrink-0 flex items-center justify-center mr-2">
        <LockClosedIcon className="size-4 text-muted-foreground" />
      </div>

      {/* Variable Name */}
      <div className="flex-shrink-0 w-45 truncate">
        <span className="text-sm font-mono text-muted-foreground">
          {variableKey}
        </span>
      </div>

      {/* Description */}
      <div className="flex-1 truncate">
        <span className="text-sm text-muted-foreground">
          Managed by Imagine
        </span>
      </div>
    </div>
  );
}

type PendingVariableItemProps = {
  variable: ProjectVariable;
  onSave: (variable: ProjectVariable) => void;
  onCancel: () => void;
};

function PendingVariableItem({
  variable,
  onSave,
  onCancel,
  existingKeys,
}: PendingVariableItemProps & { existingKeys: Array<string> }) {
  const [key, setKey] = useState(variable.key);
  const [value, setValue] = useState(variable.value);

  const trimmedKey = key.trim();
  const trimmedValue = value.trim();

  // Validation
  const isKeyEmpty = trimmedKey === "";
  const isValueEmpty = trimmedValue === "";
  const isDuplicate = existingKeys.includes(trimmedKey);
  const isValid = !isKeyEmpty && !isValueEmpty && !isDuplicate;

  // Error state for visual feedback
  const keyError = !isKeyEmpty && isDuplicate;

  const handleSave = () => {
    if (isValid) {
      onSave({ key: trimmedKey, value: trimmedValue });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="flex items-center gap-1 pl-1 h-9 flex-shrink-0 group">
      {/* Variable Name */}
      <div className="flex-shrink-0 w-45">
        <Input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`h-7 text-sm font-mono ${keyError ? "border-destructive" : ""}`}
          placeholder="VARIABLE_KEY"
        />
      </div>

      {/* Variable Value */}
      <div className="flex-1 min-w-0">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-7 text-sm font-mono"
          placeholder="value"
        />
      </div>

      {/* Actions */}
      <div className="items-center gap-1 flex">
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          className="size-6"
          type="button"
        >
          <XMarkIcon className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleSave}
          className="size-6"
          type="button"
          disabled={!isValid}
        >
          <CheckIcon
            className={`size-3.5 ${isValid ? "text-green-600" : "text-muted-foreground"}`}
          />
        </Button>
      </div>
    </div>
  );
}

export function VariablesList({
  initialVariables,
  onSave,
  isSaving,
  managedVariables = [],
}: {
  initialVariables: Array<ProjectVariable>;
  onSave: (
    variables: Array<ProjectVariable>,
  ) => Promise<Array<ProjectVariable>>;
  isSaving: boolean;
  managedVariables?: Array<ManagedVariable>;
}) {
  const form = useForm<VariablesFormData>({
    resolver: standardSchemaResolver(variablesFormSchema),
    defaultValues: {
      variables: initialVariables,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "variables",
  });

  const [pendingVariable, setPendingVariable] =
    useState<ProjectVariable | null>(null);
  const [isAnyVariableEditing, setIsAnyVariableEditing] = useState(false);

  const currentValues = form.watch("variables");
  const { isDirty } = form.formState;

  // Sort managed variables alphabetically
  const sortedManagedVariables = [...managedVariables].sort((a, b) =>
    a.key.localeCompare(b.key),
  );

  // Sort fields by variable name
  const sortedFields = [...fields]
    .map((field, originalIndex) => ({
      field,
      originalIndex,
      key: currentValues[originalIndex]?.key || "",
    }))
    .sort((a, b) => a.field.key.localeCompare(b.field.key));

  // Check if there are actual changes
  const hasChanges = isDirty;
  const canSave = hasChanges && !isAnyVariableEditing && !pendingVariable;

  const onAddVariable = () => {
    setPendingVariable({ key: "", value: "" });
  };

  const handleSavePendingVariable = (variable: ProjectVariable) => {
    append(variable);
    setPendingVariable(null);
  };

  const handleCancelPendingVariable = () => {
    setPendingVariable(null);
  };

  const handleSave = form.handleSubmit(async (data) => {
    const patchedVariables = await onSave(data.variables);
    form.reset({ variables: patchedVariables });
  });

  const handleRevert = () => {
    form.reset({ variables: initialVariables });
    setPendingVariable(null);
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSave}>
        <div className="flex flex-col w-full bg-background gap-4">
          {/* Header */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onAddVariable}
              className="gap-2"
              type="button"
            >
              <PlusIcon className="size-4" />
              New Variable
            </Button>
          </div>

          {/* Variables List */}
          <div className="flex flex-col gap-1 max-h-200 overflow-y-scroll pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full scrollbar-gutter-stable">
            {/* Pending new variable */}
            {pendingVariable && (
              <PendingVariableItem
                variable={pendingVariable}
                onSave={handleSavePendingVariable}
                onCancel={handleCancelPendingVariable}
                existingKeys={currentValues.map((v) => v.key)}
              />
            )}

            {/* Managed variables */}
            {sortedManagedVariables.map((variable) => (
              <ManagedVariableItem
                key={variable.key}
                variableKey={variable.key}
              />
            ))}

            {/* Existing variables */}
            {sortedFields.map(({ field, originalIndex }) => (
              <VariableItem
                key={field.id}
                index={originalIndex}
                onDelete={remove}
                onEditingChange={setIsAnyVariableEditing}
              />
            ))}
          </div>
          {hasChanges && (
            <div className="flex justify-end gap-2">
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRevert}
                  type="button"
                  disabled={isSaving || !canSave}
                >
                  Restore
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  type="submit"
                  loading={isSaving}
                  loadingText="Saving..."
                  disabled={!canSave}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
